import { ChangeCashMode } from '../model';
import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { Card, Iconify, Stack, Typography } from '@pflegenavi/web-components';
import { Divider } from '@mui/material';
import type { CashListIconComponent } from '@pflegenavi/web-components';
import {
  CashChangeAvatarIcon,
  IconWrapperStyle,
} from '../icons/ChangeCashIcons';

interface ChangeSummaryCardProps {
  mode: ChangeCashMode;
  cashListName: string;
  Icon: CashListIconComponent;
  /** Current total balance of the cash list in cents (cash + bank). */
  currentBalance: number;
  /** Signed change in cents: positive for deposits, negative for withdrawals. */
  amount: number;
  /** Drops the icon + mode title row; the surrounding step already names it. */
  hideHeader?: boolean;
}

export const ChangeSummaryCard: FC<ChangeSummaryCardProps> = ({
  mode,
  cashListName,
  Icon,
  currentBalance,
  amount,
  hideHeader,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const title =
    mode === ChangeCashMode.Adjust
      ? t('cashManagement.account.adjustment')
      : mode === ChangeCashMode.Withdraw
      ? t('common.withdrawal')
      : t('common.deposit');

  const amountColor =
    amount === 0 ? 'grey.400' : amount < 0 ? 'error.main' : 'success.main';

  return (
    <Card
      sx={{
        p: 2,
        width: 'fit-content',
      }}
    >
      <Stack
        direction="column"
        sx={{
          gap: 1,
        }}
      >
        {!hideHeader && (
          <>
            <Stack
              direction="row"
              sx={{
                gap: 1,
                alignItems: 'center',
              }}
            >
              <CashChangeAvatarIcon>
                <Icon width={20} height={20} />
              </CashChangeAvatarIcon>
              <Typography variant="subtitle2">{title}</Typography>
            </Stack>
            <Divider />
          </>
        )}
        <Stack
          direction="row"
          sx={{
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle2" color="grey">
            {t('cashManagement.account.current-balance', {
              name: cashListName,
            })}
          </Typography>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              pl: 8,
            }}
          >
            <Typography variant="subtitle2" color="grey">
              {fCurrencyCents(currentBalance)}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle2">{t('common.amount')}</Typography>
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            {amount !== 0 && (
              <IconWrapperStyle mode={mode} amount={amount}>
                <Iconify
                  width={16}
                  height={16}
                  icon={`eva:trending-${amount < 0 ? 'down' : 'up'}-fill`}
                />
              </IconWrapperStyle>
            )}
            <Typography variant="subtitle2" color={amountColor}>
              {fCurrencyCents(amount)}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction="row"
          sx={{
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle2">
            {t('cashManagement.account.new-balance', { name: cashListName })}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
            }}
          >
            {fCurrencyCents(currentBalance + amount)}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};
